import { useCallback, useEffect, useMemo, useState } from "react"
import axios from "axios"
import { toast } from "sonner"
import {
  BarChart3,
  Loader2,
  Plus,
  Search,
  Edit2,
  ToggleLeft,
  ToggleRight,
  Copy,
  Settings2,
} from "lucide-react"
import { Badge } from "shared/ui/badge"
import { Button } from "shared/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "shared/ui/card"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "shared/ui/dialog"
import { Input } from "shared/ui/input"
import { Label } from "shared/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "shared/ui/select"
import { Textarea } from "shared/ui/textarea"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "shared/ui/table"
import { DEFAULT_TENANT_ID } from "shared/utils/tenantContext"
import {
  activateReportTemplate,
  cloneReportTemplate,
  createReportTemplate,
  searchReportTemplates,
  updateReportTemplate,
  type ReportTemplateDto,
  type ReportTemplateStatusFilter,
} from "../api/reportTemplateService"

interface DynamicReportConfigurationProps {
  currentUser?: {
    name?: string
    tenantId?: string
    tenants?: Array<{ tenantId?: string | null; tenantName?: string | null; name?: string | null }>
  }
  onConfigure?: (reportTemplateId: string, tenantId: string) => void
  onRun?: (reportTemplateId: string, tenantId: string) => void
}

type FormState = {
  tenantId: string
  code: string
  name: string
  description: string
  dataSourceType: string
  isActive: boolean
}

type TenantOption = {
  tenantId: string
  name: string
}

const STATUS_OPTIONS: Array<{ value: ReportTemplateStatusFilter; label: string }> = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
]

const PAGE_SIZE_OPTIONS = ["10", "20", "50", "100"]

const DATA_SOURCE_OPTIONS = [
  { value: "PERMIT", label: "Permit" },
  { value: "WORKFLOW", label: "Workflow" },
  { value: "CONTRACTOR", label: "Contractor" },
]

const buildTenantOptions = (currentUser?: DynamicReportConfigurationProps["currentUser"]): TenantOption[] => {
  const rawTenants = currentUser?.tenants
  if (Array.isArray(rawTenants) && rawTenants.length > 0) {
    return rawTenants
      .map((tenant) => ({
        tenantId: tenant.tenantId ?? "",
        name: tenant.tenantName ?? tenant.name ?? tenant.tenantId ?? "",
      }))
      .filter((tenant) => tenant.tenantId)
  }

  const fallbackTenantId = currentUser?.tenantId ?? DEFAULT_TENANT_ID
  return [{ tenantId: fallbackTenantId, name: fallbackTenantId }]
}

const initialForm = (tenantId: string): FormState => ({
  tenantId,
  code: "",
  name: "",
  description: "",
  dataSourceType: "PERMIT",
  isActive: true,
})

const formatDate = (value?: string | null) => {
  if (!value) return "-"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "-"
  return date.toLocaleString("th-TH", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  })
}

export default function DynamicReportConfiguration({ currentUser, onConfigure, onRun }: DynamicReportConfigurationProps) {
  const tenantOptions = useMemo(() => buildTenantOptions(currentUser), [currentUser])
  const [selectedTenantId, setSelectedTenantId] = useState<string>(tenantOptions[0]?.tenantId ?? DEFAULT_TENANT_ID)
  const [items, setItems] = useState<ReportTemplateDto[]>([])
  const [totalItems, setTotalItems] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState<ReportTemplateStatusFilter>("all")
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<ReportTemplateDto | null>(null)
  const [form, setForm] = useState<FormState>(initialForm(tenantOptions[0]?.tenantId ?? DEFAULT_TENANT_ID))

  const isEditing = Boolean(editing)
  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalItems / pageSize)), [totalItems, pageSize])

  useEffect(() => {
    if (!tenantOptions.some((tenant) => tenant.tenantId === selectedTenantId)) {
      setSelectedTenantId(tenantOptions[0]?.tenantId ?? DEFAULT_TENANT_ID)
    }
  }, [selectedTenantId, tenantOptions])

  const loadTemplates = useCallback(
    async (nextPage = 1) => {
      if (!selectedTenantId) {
        setItems([])
        setTotalItems(0)
        return
      }

      setIsLoading(true)
      try {
        const result = await searchReportTemplates({
          tenantId: selectedTenantId,
          search: searchTerm.trim() || undefined,
          status: statusFilter,
          page: nextPage,
          pageSize,
        })
        setItems(result.items ?? [])
        setTotalItems(result.totalItems ?? result.items.length)
        setPage(result.pageNumber ?? nextPage)
      } catch (error) {
        console.error(error)
        toast.error("Failed to load report templates")
      } finally {
        setIsLoading(false)
      }
    },
    [pageSize, searchTerm, selectedTenantId, statusFilter],
  )

  useEffect(() => {
    loadTemplates(1)
  }, [loadTemplates])

  const handleOpenCreate = () => {
    setEditing(null)
    setForm(initialForm(selectedTenantId || tenantOptions[0]?.tenantId || DEFAULT_TENANT_ID))
    setDialogOpen(true)
  }

  const handleOpenEdit = (item: ReportTemplateDto) => {
    setEditing(item)
    setForm({
      tenantId: item.tenantId || selectedTenantId || DEFAULT_TENANT_ID,
      code: item.code ?? "",
      name: item.name ?? "",
      description: item.description ?? "",
      dataSourceType: item.dataSourceType ?? "PERMIT",
      isActive: item.isActive,
    })
    setDialogOpen(true)
  }

  const handleClearFilters = () => {
    setSearchTerm("")
    setStatusFilter("all")
    loadTemplates(1)
  }

  const handlePageSizeChange = (value: string) => {
    const parsed = Number.parseInt(value, 10)
    if (Number.isNaN(parsed)) return
    setPageSize(parsed)
    loadTemplates(1)
  }

  const handleSubmit = async () => {
    if (!form.tenantId.trim()) {
      toast.error("Tenant is required")
      return
    }
    if (!form.code.trim()) {
      toast.error("Code is required")
      return
    }
    if (!form.name.trim()) {
      toast.error("Template name is required")
      return
    }
    if (!form.dataSourceType.trim()) {
      toast.error("Data source type is required")
      return
    }

    const payload = {
      tenantId: form.tenantId.trim(),
      code: form.code.trim().toUpperCase(),
      name: form.name.trim(),
      description: form.description.trim() || null,
      dataSourceType: form.dataSourceType.trim().toUpperCase(),
      isActive: form.isActive,
    }

    setIsSubmitting(true)
    try {
      if (isEditing && editing) {
        await updateReportTemplate(editing.reportTemplateId, payload)
        toast.success("Updated report template")
      } else {
        await createReportTemplate(payload)
        toast.success("Created report template")
      }
      setSelectedTenantId(form.tenantId.trim())
      setDialogOpen(false)
      loadTemplates(page)
    } catch (error: any) {
      console.error(error)
      const message =
        error?.response?.data?.message ??
        error?.response?.data ??
        "Failed to save report template"
      toast.error(typeof message === "string" ? message : "Failed to save report template")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleToggleActive = async (item: ReportTemplateDto) => {
    const nextStatus = !item.isActive
    try {
      await activateReportTemplate(item.reportTemplateId, item.tenantId || selectedTenantId || DEFAULT_TENANT_ID, nextStatus)
      toast.success(`Template ${nextStatus ? "activated" : "inactivated"}`)
      loadTemplates(page)
    } catch (error) {
      if (axios.isAxiosError(error) && [404, 405].includes(error.response?.status ?? 0)) {
        try {
          await updateReportTemplate(item.reportTemplateId, {
            tenantId: item.tenantId || selectedTenantId || DEFAULT_TENANT_ID,
            code: item.code,
            name: item.name,
            description: item.description ?? null,
            dataSourceType: item.dataSourceType,
            isActive: nextStatus,
          })
          toast.success(`Template ${nextStatus ? "activated" : "inactivated"}`)
          loadTemplates(page)
          return
        } catch (fallbackError: any) {
          console.error(fallbackError)
          const message =
            fallbackError?.response?.data?.message ??
            fallbackError?.response?.data ??
            "Failed to update template status"
          toast.error(typeof message === "string" ? message : "Failed to update template status")
          return
        }
      }

      console.error(error)
      toast.error("Failed to update template status")
    }
  }

  const handleClone = async (item: ReportTemplateDto) => {
    try {
      await cloneReportTemplate(item.reportTemplateId, item.tenantId || selectedTenantId || DEFAULT_TENANT_ID)
      toast.success("Cloned report template")
      loadTemplates(1)
    } catch (error: any) {
      if (axios.isAxiosError(error) && [404, 405].includes(error.response?.status ?? 0)) {
        try {
          const suffix = Date.now().toString().slice(-4)
          await createReportTemplate({
            tenantId: item.tenantId || selectedTenantId || DEFAULT_TENANT_ID,
            code: `${item.code}_COPY_${suffix}`,
            name: `${item.name} (Copy)`,
            description: item.description ?? null,
            dataSourceType: item.dataSourceType,
            isActive: false,
          })
          toast.success("Cloned report template")
          loadTemplates(1)
          return
        } catch (fallbackError: any) {
          console.error(fallbackError)
          const fallbackMessage =
            fallbackError?.response?.data?.message ??
            fallbackError?.response?.data ??
            "Failed to clone report template"
          toast.error(typeof fallbackMessage === "string" ? fallbackMessage : "Failed to clone report template")
          return
        }
      }

      console.error(error)
      const message =
        error?.response?.data?.message ??
        error?.response?.data ??
        "Failed to clone report template"
      toast.error(typeof message === "string" ? message : "Failed to clone report template")
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-xl">
              <BarChart3 className="h-5 w-5 text-purple-600" />
              Dynamic Report Configuration
            </CardTitle>
            <p className="text-sm text-gray-500">
              Manage report templates and data-source configuration
              {currentUser?.name ? ` (admin: ${currentUser.name})` : ""}
            </p>
          </div>
          <Button onClick={handleOpenCreate} className="gap-2">
            <Plus className="h-4 w-4" />
            Create Template
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Select value={selectedTenantId} onValueChange={setSelectedTenantId}>
              <SelectTrigger className="w-full md:w-[260px]">
                <SelectValue placeholder="Select tenant" />
              </SelectTrigger>
              <SelectContent>
                {tenantOptions.map((tenant) => (
                  <SelectItem key={tenant.tenantId} value={tenant.tenantId}>
                    {tenant.name || tenant.tenantId}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative w-full md:w-80">
              <Search className="h-4 w-4 text-gray-400 absolute left-3 top-3" />
              <Input
                placeholder="Search by code / name / description"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                onKeyDown={(event) => event.key === "Enter" && loadTemplates(1)}
                className="pl-9"
              />
            </div>

            <Select value={statusFilter} onValueChange={(value: ReportTemplateStatusFilter) => setStatusFilter(value)}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button variant="outline" onClick={() => loadTemplates(1)} disabled={isLoading}>
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
            </Button>
            <Button variant="ghost" onClick={handleClearFilters} disabled={isLoading}>
              Clear
            </Button>
            <div className="text-sm text-gray-500">Total {totalItems.toLocaleString("en-US")} items</div>
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Template Name</TableHead>
                  <TableHead>Data Source</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="h-28 text-center text-gray-500">
                      <div className="inline-flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading report templates...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="h-28 text-center text-gray-500">
                      No report templates found
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => (
                    <TableRow key={item.reportTemplateId}>
                      <TableCell className="font-medium">{item.code}</TableCell>
                      <TableCell>
                        <div className="font-medium text-gray-900">{item.name}</div>
                        {item.description ? (
                          <div className="text-xs text-gray-500 line-clamp-2">{item.description}</div>
                        ) : null}
                      </TableCell>
                      <TableCell>{item.dataSourceType || "-"}</TableCell>
                      <TableCell>v{item.version ?? 1}</TableCell>
                      <TableCell>
                        <Badge
                          variant="secondary"
                          className={
                            item.isActive
                              ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100"
                              : "bg-gray-200 text-gray-600 hover:bg-gray-200"
                          }
                        >
                          {item.isActive ? "ACTIVE" : "INACTIVE"}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(item.createdAt)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button size="sm" variant="outline" className="gap-1" onClick={() => handleOpenEdit(item)}>
                            <Edit2 className="h-3.5 w-3.5" />
                            Edit
                          </Button>
                          <Button size="sm" variant="outline" className="gap-1" onClick={() => handleClone(item)}>
                            <Copy className="h-3.5 w-3.5" />
                            Clone
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1"
                            onClick={() => onConfigure?.(item.reportTemplateId, item.tenantId || selectedTenantId || DEFAULT_TENANT_ID)}
                            disabled={!onConfigure}
                          >
                            <Settings2 className="h-3.5 w-3.5" />
                            Configure
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1"
                            onClick={() => onRun?.(item.reportTemplateId, item.tenantId || selectedTenantId || DEFAULT_TENANT_ID)}
                            disabled={!onRun}
                          >
                            <BarChart3 className="h-3.5 w-3.5" />
                            Run
                          </Button>
                          <Button size="sm" variant="outline" className="gap-1" onClick={() => handleToggleActive(item)}>
                            {item.isActive ? (
                              <>
                                <ToggleLeft className="h-3.5 w-3.5" />
                                Inactive
                              </>
                            ) : (
                              <>
                                <ToggleRight className="h-3.5 w-3.5" />
                                Active
                              </>
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-gray-500">
              Showing {totalItems === 0 ? 0 : (page - 1) * pageSize + 1} - {Math.min(page * pageSize, totalItems)} of{" "}
              {totalItems.toLocaleString("en-US")} records
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm text-gray-500">Page size</Label>
              <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                <SelectTrigger className="w-[86px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <SelectItem key={size} value={size}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" disabled={page <= 1 || isLoading} onClick={() => loadTemplates(page - 1)}>
                Prev
              </Button>
              <span className="text-sm text-gray-700">
                Page {page} / {totalPages}
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={page >= totalPages || isLoading}
                onClick={() => loadTemplates(page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{isEditing ? "Edit Report Template" : "Create Report Template"}</DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Tenant *</Label>
              <Select
                value={form.tenantId}
                onValueChange={(value) => setForm((prev) => ({ ...prev, tenantId: value }))}
                disabled={isEditing}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select tenant" />
                </SelectTrigger>
                <SelectContent>
                  {tenantOptions.map((tenant) => (
                    <SelectItem key={tenant.tenantId} value={tenant.tenantId}>
                      {tenant.name || tenant.tenantId}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Code *</Label>
              <Input
                value={form.code}
                onChange={(event) => setForm((prev) => ({ ...prev, code: event.target.value }))}
                placeholder="PERMIT_SUMMARY"
                disabled={isEditing}
              />
            </div>
            <div className="space-y-2">
              <Label>Template Name *</Label>
              <Input
                value={form.name}
                onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                placeholder="Permit Summary Report"
              />
            </div>
            <div className="space-y-2">
              <Label>Data Source *</Label>
              <Select
                value={form.dataSourceType}
                onValueChange={(value) => setForm((prev) => ({ ...prev, dataSourceType: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select data source type" />
                </SelectTrigger>
                <SelectContent>
                  {DATA_SOURCE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={form.isActive ? "active" : "inactive"}
                onValueChange={(value) => setForm((prev) => ({ ...prev, isActive: value === "active" }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                placeholder="Describe purpose and usage of this report template"
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSubmit} disabled={isSubmitting} className="gap-2">
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
